import dayjs from "dayjs";
import * as DATE_PERIOD from "constants/dateFilter";

export const ZOHO_BOOKS = "Zoho Books";
export const QUICK_BOOKS = "QuickBooks";

export const ACCOUNTING_METHODS = {
  ACCRUAL: "ACCRUAL",
  CASH: "CASH",
} as const;

export const CURRENT_DUE_RANGE = "CURRENT DUE";
export const AGING_RANGE_AND_MORE_SUFFIX = "AND MORE";

export type ACCOUNTING_METHOD_TYPES =
  (typeof ACCOUNTING_METHODS)[keyof typeof ACCOUNTING_METHODS];

export const SUMMARY_REPORT = {
  VENDOR: "VENDOR",
  CATEGORY: "CATEGORY",
} as const;

export type SUMMARY_REPORT_TYPES =
  (typeof SUMMARY_REPORT)[keyof typeof SUMMARY_REPORT];

export const STRIPE_FREQUENCY = {
  DAILY: "DAILY",
  MONTHLY: "MONTHLY",
} as const;

export type STRIPE_FREQUENCY_TYPES =
  (typeof STRIPE_FREQUENCY)[keyof typeof STRIPE_FREQUENCY];

export const TAX_YEAR_END = {
  FEB_JAN: "FEB_JAN",
  MAR_FEB: "MAR_FEB",
  APR_MAR: "APR_MAR",
  MAY_APR: "MAY_APR",
  JUN_MAY: "JUN_MAY",
  JUL_JUN: "JUL_JUN",
  AUG_JUL: "AUG_JUL",
  SEP_AUG: "SEP_AUG",
  OCT_SEP: "OCT_SEP",
  NOV_OCT: "NOV_OCT",
  DEC_NOV: "DEC_NOV",
  JAN_DEC: "JAN_DEC",
} as const;

export type TAX_YEAR_END_TYPES =
  (typeof TAX_YEAR_END)[keyof typeof TAX_YEAR_END];

export const MONTH_INDEX: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

export const ACCOUNTING_METHODS_OPTIONS = [
  { label: "Accrual", value: ACCOUNTING_METHODS.ACCRUAL },
  { label: "Cash", value: ACCOUNTING_METHODS.CASH },
];

export const ACCOUNTING_METHOD_LABELS = {
  [ACCOUNTING_METHODS.ACCRUAL]: "Accrual method",
  [ACCOUNTING_METHODS.CASH]: "Cash method",
};

// COA
export const ASSET = 1;
export const LIABILITY = 2;
export const EQUITY = 3;
export const REVENUE = 4;
export const EXPENSE = 5;

// REPORTS
export const BALANCE_SHEET = "BALANCE_SHEET";
export const INCOME_STATEMENT = "INCOME_STATEMENT";
export const CASH_FLOW_STATEMENT = "CASH_FLOW";
export const SAVED_REPORTS = "SAVED_REPORTS";
export const GENERAL_LEDGER = "GENERAL_LEDGER";
export const TRIAL_BALANCE = "TRIAL_BALANCE";
export const AR_AGING_SUMMARY = "AR_AGING";
export const AP_AGING_SUMMARY = "AP_AGING";
export const REVENUE_RECOGNITION = "REVENUE_RECOGNITION";
export const VENDOR_SUMMARY = "VENDOR_SUMMARY";
export const CUSTOM_REPORT = "CUSTOM_REPORT";

export const REPORTS_LABELS = {
  GENERAL_LEDGER: "General Ledger",
  TRIAL_BALANCE: "Trial Balance",
} as const;

export const REPORT_TYPES = {
  BALANCE_SHEET,
  INCOME_STATEMENT,
  CASH_FLOW: CASH_FLOW_STATEMENT,
  GENERAL_LEDGER,
  TRIAL_BALANCE,
  AR_AGING_SUMMARY,
  AP_AGING_SUMMARY,
  REVENUE_RECOGNITION,
  VENDOR_SUMMARY,
  CUSTOM_REPORT,
} as const;

export type ReportType = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];

export const EXPORT_FORMAT = {
  PDF: "PDF",
  EXCEL: "EXCEL",
} as const;

export type ExportFormatType =
  (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

export const FINANCIAL_STATEMENT_REPORTS = [
  BALANCE_SHEET,
  INCOME_STATEMENT,
  CASH_FLOW_STATEMENT,
] as const;

export const AGING_SUMMARY_REPORTS = [
  AR_AGING_SUMMARY,
  AP_AGING_SUMMARY,
] as const;

export const SIMPLE_REPORTS = [
  GENERAL_LEDGER,
  TRIAL_BALANCE,
  VENDOR_SUMMARY,
] as const;

export const EXCEL_ONLY_REPORTS = [
  AR_AGING_SUMMARY,
  AP_AGING_SUMMARY,
  REVENUE_RECOGNITION,
  VENDOR_SUMMARY,
] as const;

export const REPORTS_WITH_GROUP_BY = [
  BALANCE_SHEET,
  INCOME_STATEMENT,
  CASH_FLOW_STATEMENT,
  TRIAL_BALANCE,
  REVENUE_RECOGNITION,
] as const;

export const AVAILABLE_REPORTS = [
  { label: "Balance Sheet", value: BALANCE_SHEET },
  { label: "Income Statement", value: INCOME_STATEMENT },
  { label: "Cash Flow Statement", value: CASH_FLOW_STATEMENT },
  { label: "General Ledger", value: GENERAL_LEDGER },
  { label: "Trial Balance", value: TRIAL_BALANCE },
  { label: "AR Aging", value: AR_AGING_SUMMARY },
  { label: "AP Aging", value: AP_AGING_SUMMARY },
  { label: "Revenue Recognition Report", value: REVENUE_RECOGNITION },
  { label: "Vendor Summary", value: VENDOR_SUMMARY },
] as const;

export const GROUP_BY_OPTIONS = [
  { label: "None", value: "" },
  { label: "Customer", value: "CUSTOMER" },
  { label: "Vendor", value: "VENDOR" },
  { label: "Account", value: "ACCOUNT" },
] as const;

export const DOWNLOAD_FORMAT = {
  DOWNLOAD_PDF: "DOWNLOAD_PDF",
  DOWNLOAD_EXCEL: "DOWNLOAD_EXCEL",
} as const;

export type DOWNLOAD_FORMAT_TYPES = keyof typeof DOWNLOAD_FORMAT;

export const REPORTS_STATE = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export type REPORTS_STATE_TYPES = keyof typeof REPORTS_STATE;

// DASHBOARD
export const CONNECTIONS = "Connections";
export const NEW_ACCOUNTS_DETECTED = "New Accounts Detected";
export const RECONCILIATION = "Reconciliation";
export const PENDING_REVIEW_TRANSACTIONS = "Pending Review Transactions";
export const FINANCIAL_CLOSING = "Financial Closing";
export const CHAT_REQUESTS = "Chat Requests";
export const INFO_REQUESTED = "Info Requested";
export const DOCUMENT_REQUESTED = "Document Requested";
export const DOCUMENTS_REQUESTED = "Documents Requested";

//COA
export const STANDARD = "STANDARD";

// Date Filter
export const datePeriod = [
  { label: "Last 30 days", value: DATE_PERIOD.LAST_30_DAYS },
  { label: "Last 90 days", value: DATE_PERIOD.LAST_90_DAYS },
  { label: "Current month", value: DATE_PERIOD.CURRENT_MONTH },
  { label: "Last month", value: DATE_PERIOD.LAST_MONTH },
  { label: "Last 3 months", value: DATE_PERIOD.LAST_3_MONTHS },
  {
    label: `Current year - ${dayjs().get("year")}`,
    value: DATE_PERIOD.CURRENT_YEAR,
  },
  {
    label: `Last year - ${dayjs().subtract(1, "year").get("year")}`,
    value: DATE_PERIOD.LAST_YEAR,
  },
];

export const CUSTOM_PERIOD = "CUSTOM";
export const START_DATE_KEY = "Since";
export const END_DATE_KEY = "Till";

export const monthPeriod = [
  { label: "This month", value: DATE_PERIOD.CURRENT_MONTH },
  { label: "Last month", value: DATE_PERIOD.LAST_MONTH },
  { label: "Last 3 months", value: DATE_PERIOD.LAST_3_MONTHS },
  { label: "Last 6 months", value: DATE_PERIOD.LAST_6_MONTHS },
  { label: "Last 12 months", value: DATE_PERIOD.LAST_12_MONTHS },
];

// Bookkeeping Tracker
export const IN_PROGRESS = "IN_PROGRESS";
export const COMPLETED = "COMPLETED";

export const SERVICE_TEAM_LABEL: { [key: string]: string } = {
  INKLE_TEAM: "Inkle",
  CPA_TEAM: "Tranquility",
};

export const TXN_GROUP_BY = {
  VENDOR: "vendor",
} as const;

export type TxnGroupBy = (typeof TXN_GROUP_BY)[keyof typeof TXN_GROUP_BY];

export const TXN_GROUP_BY_SORT_COL: Record<
  TxnGroupBy,
  "DATE" | "VENDOR" | "AMOUNT"
> = {
  [TXN_GROUP_BY.VENDOR]: "VENDOR",
};

export const cashFlowPeriod = [
  { label: "Last 3 months", value: DATE_PERIOD.LAST_3_MONTHS },
  { label: "Last 6 months", value: DATE_PERIOD.LAST_6_MONTHS },
  { label: "Last 12 months", value: DATE_PERIOD.LAST_12_MONTHS },
];
